import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  ListTodo, 
  Bell, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Producteev = () => {
  const faqData = [
    {
      question: "What happened to Producteev?",
      answer: "Producteev was acquired by Jive Software in 2012, then shut down on May 11, 2018 after Jive was acquired by Aurea. The shutdown came with minimal notice, leaving teams scrambling."
    },
    {
      question: "Is there a free alternative to Producteev?",
      answer: "Yes! CSW offers free task management features along with study timers, reminders, and collaborative study rooms for teams and individuals."
    },
    {
      question: "What made Producteev special?",
      answer: "Producteev pioneered 'social task management'—letting teams assign tasks to multiple people, attach files, and collaborate on shared projects. In 2013, it became completely free for unlimited users."
    },
    {
      question: "How does CSW compare to Producteev?",
      answer: "CSW offers collaborative task management and real-time co-working sessions. While Producteev focused on asynchronous task tracking, CSW adds live accountability through virtual study rooms."
    },
    {
      question: "Does CSW work for teams like Producteev did?",
      answer: "Absolutely! CSW's collaborative study rooms let teams work together in real-time, share goals, and hold each other accountable—taking team productivity beyond what Producteev offered."
    },
    {
      question: "Is CSW free like Producteev was?",
      answer: "Yes, CSW is completely free with unlimited access to all features—no subscription required."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Producteev Alternative - Free Task Management & Study Tools",
    "description": "Looking for a Producteev alternative? Producteev shut down in 2018 but CSW offers free Task Management, collaborative study rooms, and productivity tools.",
    "url": "https://chillstudy.work/producteev/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Producteev",
      "applicationCategory": "ProductivityApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "Asana",
        "url": "https://asana.com/"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Trello",
        "url": "https://trello.com/"
      }
    ],
    "keywords": "Producteev alternative, Producteev replacement, task management app, Jive Producteev, team collaboration, productivity tools"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work - Producteev Alternative",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Team Task Management",
      "Collaborative Study Rooms",
      "Pomodoro Study Timer",
      "Progress Tracking",
      "Cross-Platform Sync",
      "Real-time Accountability",
      "Focus Mode",
      "Reminders"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Producteev Alternative - Free Task Management & Team Collaboration | CSW</title>
        <meta name="description" content="Looking for a Producteev alternative? Producteev shut down in 2018 but CSW offers free Task Management, collaborative study rooms, and productivity tools." />
        <meta name="keywords" content="Producteev alternative, Producteev replacement, task management app, Jive Producteev, team collaboration, productivity tools" />
        <link rel="canonical" href="https://chillstudy.work/producteev/" />
        <meta property="og:title" content="Producteev Alternative - Free Task Management & Team Collaboration" />
        <meta property="og:description" content="Producteev shut down in 2018. Discover CSW's free task management, collaborative rooms, and productivity tools." />
        <meta property="og:url" content="https://chillstudy.work/producteev/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Producteev shut down on May 11, 2018
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              The Best Producteev Alternative for Team Productivity
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Miss Producteev's social task management? CSW combines collaborative task tracking with 
              <Link to="/study-with-me/" className="text-primary hover:underline"> real-time study sessions</Link>, 
              team accountability, and productivity tools—all for free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/producteev/history/">
                  Read Producteev's Story
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">What Happened to Producteev?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <a href="https://tracxn.com/d/companies/producteev" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Producteev</a> was 
              a pioneering social task management platform founded in 2008 by Ilan Abehassera. It helped teams manage tasks collaboratively 
              and became one of the first tools to treat tasks as social objects with comments, assignments, and file attachments.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              In 2012, <a href="https://techcrunch.com/2012/11/05/jive-software-acquires-meetings-io-and-producteev/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Jive Software acquired Producteev</a>, 
              and in 2013 made it completely free for unlimited users. However, after Jive was acquired by Aurea in 2017, Producteev 
              was shut down on May 11, 2018 with minimal notice.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              CSW offers <Link to="/task-management/" className="text-primary hover:underline">task management</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">study timers</Link>,{" "}
              <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>, and{" "}
              <Link to="/group-study/" className="text-primary hover:underline">collaborative study rooms</Link>.
            </p>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Everything You Loved About Producteev, Plus More
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <ListTodo className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Create tasks, set priorities, and organize projects just like Producteev. 
                  <Link to="/task-management/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Team Collaboration</h3>
                <p className="text-muted-foreground">
                  Work together in real-time study rooms—taking Producteev's team features further.
                  <Link to="/study-with-friends/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-green-500/10 rounded-lg w-fit mb-4">
                  <Clock className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Focus Timer</h3>
                <p className="text-muted-foreground">
                  Built-in Pomodoro timer to help your team stay focused—something Producteev never had.
                  <Link to="/study-timer/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-purple-500/10 rounded-lg w-fit mb-4">
                  <Bell className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Smart Reminders</h3>
                <p className="text-muted-foreground">
                  Never miss a deadline with customizable reminders and notifications.
                  <Link to="/reminders/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ambient Sounds</h3>
                <p className="text-muted-foreground">
                  Focus better with lo-fi beats, rain sounds, and café ambiance.
                  <Link to="/studystream/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-blue-500/10 rounded-lg w-fit mb-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Progress Tracking</h3>
                <p className="text-muted-foreground">
                  Track your team's productivity and completed tasks to build better habits.
                  <Link to="/productivity-tool/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Alternatives */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Other Producteev Alternatives</h2>
            <p className="text-muted-foreground mb-6">
              While CSW focuses on collaborative productivity for students and teams, here are other project management 
              apps that former Producteev users have migrated to:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="https://asana.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Asana</p>
                  <p className="text-sm text-muted-foreground">Full-featured project management</p>
                </div>
              </a>
              <a href="https://trello.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Trello</p>
                  <p className="text-sm text-muted-foreground">Visual Kanban boards</p>
                </div>
              </a>
              <a href="https://clickup.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">ClickUp</p>
                  <p className="text-sm text-muted-foreground">All-in-one productivity platform</p>
                </div>
              </a>
              <a href="https://www.paymoapp.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Paymo</p>
                  <p className="text-sm text-muted-foreground">Work management with time tracking</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Blog Links */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Productivity Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/how-to-stop-procrastination/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How to Stop Procrastination</p>
                <p className="text-sm text-muted-foreground">Science-backed strategies to get things done</p>
              </Link>
              <Link to="/blog/apps-boost-organization-college/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Best Organization Apps for College</p>
                <p className="text-sm text-muted-foreground">Top apps to stay organized in school</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools every student needs</p>
              </Link>
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How Camera Boosted My Productivity</p>
                <p className="text-sm text-muted-foreground">The power of accountability</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-primary/10 rounded-lg">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Ready to Boost Team Productivity?</h2>
            <p className="text-muted-foreground mb-6">
              Join thousands of students and professionals who use CSW to stay focused and get things done together.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Producteev" />

      <Footer />
    </div>
  );
};

export default Producteev;
